<template>
  <div class="tn-auth">
    <div class="tn-auth__scene" aria-hidden="true">
      <div class="tn-auth__photo"></div>
      <div class="tn-auth__veil"></div>
      <div class="tn-auth__glow tn-auth__glow--a"></div>
      <div class="tn-auth__glow tn-auth__glow--b"></div>
      <div class="tn-auth__dots"></div>

      <!-- Dotted "journey" routes with a gold light travelling along each one -->
      <svg class="tn-auth__routes" viewBox="0 0 1600 900" preserveAspectRatio="xMidYMid slice">
        <defs>
          <radialGradient id="tn-traveller-glow">
            <stop offset="0%" stop-color="#FFE2B0" />
            <stop offset="35%" stop-color="#F2C27B" stop-opacity=".9" />
            <stop offset="100%" stop-color="#D68A2D" stop-opacity="0" />
          </radialGradient>
        </defs>
        <path id="tn-route-a" class="tn-route" d="M -60 760 C 260 610, 420 840, 760 720 S 1240 430, 1680 540" />
        <path id="tn-route-b" class="tn-route tn-route--faint" d="M 1680 120 C 1380 250, 1180 60, 900 160 S 380 330, -60 210" />
        <circle r="14" fill="url(#tn-traveller-glow)">
          <animateMotion dur="18s" repeatCount="indefinite"><mpath xlink:href="#tn-route-a" /></animateMotion>
        </circle>
        <circle r="10" fill="url(#tn-traveller-glow)">
          <animateMotion dur="24s" begin="-9s" repeatCount="indefinite"><mpath xlink:href="#tn-route-b" /></animateMotion>
        </circle>
      </svg>

      <span class="tn-auth__ring tn-auth__ring--outer"></span>
      <span class="tn-auth__ring tn-auth__ring--middle"></span>
      <span class="tn-auth__ring tn-auth__ring--inner"></span>
      <span class="tn-auth__halo"></span>

      <div class="tn-auth__dust">
        <span
          v-for="(p, i) in particles"
          :key="i"
          :style="{ left: p.left + '%', width: p.size + 'px', height: p.size + 'px', animationDuration: p.duration + 's', animationDelay: -p.delay + 's' }"
        ></span>
      </div>
    </div>

    <main class="tn-auth__stage">
      <div class="tn-auth__brand">
        <div class="tn-auth__logo">
          <img src="../../assets/brand/logo-white.svg" alt="Tanefer Experience">
          <span class="tn-auth__shine" aria-hidden="true"></span>
        </div>
        <span class="tn-auth__eyebrow">Admin Console</span>
      </div>

      <div class="tn-auth__card-frame">
        <div class="tn-auth__card">
          <slot></slot>
        </div>
      </div>

      <div class="tn-auth__copy">© {{ year }} Tanefer Tours · Packages, hotels, cruises and adventures</div>
    </main>
  </div>
</template>

<script>
// Fixed values (not random) so the gold dust looks the same on every visit.
const particles = [
  [4, 3, 19, 2], [10, 2, 24, 11], [17, 4, 21, 6], [24, 2, 27, 15], [31, 3, 18, 9],
  [38, 2, 25, 3], [45, 4, 22, 13], [52, 2, 20, 7], [59, 3, 26, 17], [66, 2, 19, 4],
  [73, 4, 23, 10], [80, 2, 21, 14], [87, 3, 25, 1], [94, 2, 28, 19], [14, 2, 29, 21],
  [42, 3, 30, 5], [70, 2, 27, 12], [97, 2, 26, 8], [27, 3, 23, 16], [56, 2, 28, 20],
  [84, 3, 24, 18], [7, 2, 22, 12]
].map(([left, size, duration, delay]) => ({ left, size, duration, delay }))

export default {
  name: 'AuthLayout',
  data () {
    return { particles }
  },
  computed: {
    year () {
      return new Date().getFullYear()
    }
  }
}
</script>

<style lang="scss">
$tn-gold: #D68A2D;
$tn-ease: cubic-bezier(.22, 1, .36, 1);

.tn-auth {
  position: relative;
  min-height: calc(100vh / var(--tn-zoom, 1));
  background: #0B1423;
  isolation: isolate;
}

// ---------- Animated background (fixed, full screen) ----------
.tn-auth__scene {
  position: fixed;
  inset: 0;
  z-index: -1;
  overflow: hidden;
  pointer-events: none;
}

// Slow "Ken Burns" drift of the temple photo.
.tn-auth__photo {
  position: absolute;
  inset: -6%;
  background: url('../../assets/brand/login-hero.jpg') center / cover no-repeat;
  animation: tn-kenburns 34s ease-in-out infinite alternate;
  will-change: transform;
}

// Navy vignette: darker at the edges, a little lighter in the middle.
.tn-auth__veil {
  position: absolute;
  inset: 0;
  background:
    radial-gradient(ellipse 65% 70% at 50% 50%, rgba(15, 27, 45, .62) 0%, rgba(15, 27, 45, .86) 60%, rgba(9, 16, 29, .96) 100%),
    linear-gradient(180deg, rgba(9, 16, 29, .45) 0%, rgba(9, 16, 29, 0) 30%, rgba(9, 16, 29, .6) 100%);
}

.tn-auth__glow {
  position: absolute;
  border-radius: 50%;
  filter: blur(50px);
  mix-blend-mode: screen;
  will-change: transform;

  &--a {
    top: 6%;
    left: 4%;
    width: 38vmax;
    height: 38vmax;
    background: radial-gradient(circle, rgba(223, 115, 28, .40) 0%, rgba(223, 115, 28, 0) 70%);
    animation: tn-drift-a 22s ease-in-out infinite alternate;
  }

  &--b {
    right: 2%;
    bottom: 0;
    width: 42vmax;
    height: 42vmax;
    background: radial-gradient(circle, rgba(214, 138, 45, .30) 0%, rgba(214, 138, 45, 0) 70%);
    animation: tn-drift-b 28s ease-in-out infinite alternate;
  }
}

// Fine dot grid that fades out towards the edges.
.tn-auth__dots {
  position: absolute;
  inset: 0;
  background-image: radial-gradient(rgba(255, 255, 255, .10) 1px, transparent 1.2px);
  background-size: 24px 24px;
  -webkit-mask-image: radial-gradient(ellipse 60% 55% at 50% 50%, #000 0%, transparent 100%);
  mask-image: radial-gradient(ellipse 60% 55% at 50% 50%, #000 0%, transparent 100%);
  opacity: .55;
}

.tn-auth__routes {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  animation: tn-fade-in 2s .6s ease-out both;
}

.tn-route {
  fill: none;
  stroke: rgba(214, 138, 45, .5);
  stroke-width: 2;
  stroke-linecap: round;
  stroke-dasharray: 1 12;
  animation: tn-route-flow 6s linear infinite;

  &--faint {
    stroke: rgba(214, 138, 45, .3);
    animation-duration: 9s;
    animation-direction: reverse;
  }
}

// Rings of a sun disc behind the card (centred on the screen).
.tn-auth__ring,
.tn-auth__halo {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  margin: auto;
  border-radius: 50%;
}

.tn-auth__ring {
  &--outer {
    width: min(980px, 150vmin);
    height: min(980px, 150vmin);
    border: 1px dashed rgba(214, 138, 45, .30);
    animation: tn-fade-in 1.8s ease-out both, tn-spin 120s linear infinite;
  }

  &--middle {
    width: min(820px, 125vmin);
    height: min(820px, 125vmin);
    border: 1px solid rgba(255, 255, 255, .07);
    border-top-color: rgba(214, 138, 45, .7);
    border-right-color: rgba(214, 138, 45, .25);
    animation: tn-fade-in 1.8s .2s ease-out both, tn-spin-reverse 34s linear infinite;
  }

  &--inner {
    width: min(680px, 104vmin);
    height: min(680px, 104vmin);
    border: 1px dotted rgba(242, 194, 123, .28);
    animation: tn-fade-in 1.8s .4s ease-out both, tn-spin 70s linear infinite;
  }
}

.tn-auth__halo {
  width: min(760px, 120vmin);
  height: min(760px, 120vmin);
  background: radial-gradient(circle, rgba(214, 138, 45, .22) 0%, rgba(214, 138, 45, .07) 42%, rgba(214, 138, 45, 0) 68%);
  animation: tn-fade-in 1.8s ease-out both, tn-breathe 6s 1.8s ease-in-out infinite alternate;
}

// Gold dust rising slowly.
.tn-auth__dust span {
  position: absolute;
  bottom: -12px;
  border-radius: 50%;
  background: #F2C27B;
  box-shadow: 0 0 6px 1px rgba(242, 194, 123, .7);
  opacity: 0;
  animation-name: tn-rise;
  animation-timing-function: linear;
  animation-iteration-count: infinite;
}

// ---------- Foreground ----------
.tn-auth__stage {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 26px;
  min-height: calc(100vh / var(--tn-zoom, 1));
  padding: 40px 16px 28px;
}

.tn-auth__brand {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.tn-auth__logo {
  position: relative;
  width: clamp(190px, 18vw, 250px);
  animation: tn-logo-in 1.3s .15s $tn-ease both;

  img {
    display: block;
    width: 100%;
    height: auto;
    filter: drop-shadow(0 6px 24px rgba(0, 0, 0, .4));
  }
}

// A gold light sweep that only paints inside the logo's letters.
.tn-auth__shine {
  position: absolute;
  inset: 0;
  background: linear-gradient(105deg, transparent 30%, rgba(255, 214, 150, .95) 47%, #FFFFFF 50%, rgba(255, 214, 150, .95) 53%, transparent 70%);
  background-size: 250% 100%;
  background-position: 150% 0;
  background-repeat: no-repeat;
  -webkit-mask: url('../../assets/brand/logo-white.svg') center / contain no-repeat;
  mask: url('../../assets/brand/logo-white.svg') center / contain no-repeat;
  animation: tn-shine 7s 2s ease-in-out infinite;
}

.tn-auth__eyebrow {
  display: flex;
  align-items: center;
  gap: 12px;
  color: #F2C27B;
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: .32em;
  text-transform: uppercase;
  animation: tn-fade-up .9s .7s $tn-ease both;

  &::before,
  &::after {
    content: '';
    width: 28px;
    height: 1px;
    background: linear-gradient(90deg, rgba(214, 138, 45, 0), $tn-gold);
  }

  &::after {
    background: linear-gradient(90deg, $tn-gold, rgba(214, 138, 45, 0));
  }
}

// The frame is a 1.5px ring around the card with a gold light orbiting it.
@property --tn-angle {
  syntax: '<angle>';
  initial-value: 0deg;
  inherits: false;
}

.tn-auth__card-frame {
  position: relative;
  width: 100%;
  max-width: 392px;
  padding: 1.5px;
  border-radius: 20px;
  background: conic-gradient(
    from var(--tn-angle),
    rgba(214, 138, 45, .18) 0deg,
    rgba(214, 138, 45, .18) 230deg,
    #D68A2D 290deg,
    #FFE2B0 318deg,
    #D68A2D 340deg,
    rgba(214, 138, 45, .18) 360deg
  );
  box-shadow:
    0 36px 80px -24px rgba(0, 0, 0, .7),
    0 0 70px -12px rgba(214, 138, 45, .35);
  animation: tn-card-in 1s .35s $tn-ease both, tn-orbit 7s linear infinite;
}

.tn-auth__card {
  position: relative;
  padding: 30px 30px 26px;
  border-radius: 18.5px;
  background:
    radial-gradient(120% 60% at 50% 0%, rgba(214, 138, 45, .07) 0%, rgba(214, 138, 45, 0) 60%),
    #FFFFFF;

  @media (max-width: 599px) {
    padding: 26px 20px 22px;
  }
}

.tn-auth__copy {
  color: rgba(255, 255, 255, .5);
  font-size: 12.5px;
  letter-spacing: .02em;
  text-align: center;
  animation: tn-fade-in 1s 1.1s ease-out both;
}

// ---------- Form content (login / reset password) ----------
.tn-auth__title {
  margin: 0 0 4px;
  color: var(--tn-navy);
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -.02em;
  text-align: center;
  animation: tn-fade-up .6s .55s $tn-ease both;
}

.tn-auth__subtitle {
  margin: 0 0 22px !important;
  color: var(--tn-muted);
  font-size: 13.5px;
  line-height: 1.5;
  text-align: center;
  animation: tn-fade-up .6s .62s $tn-ease both;
}

.tn-auth__alert.v-alert {
  margin-bottom: 16px;
  font-size: 13.5px;
}

// Each row of the form slides in a moment after the previous one.
.tn-auth__fields > * {
  animation: tn-fade-up .6s $tn-ease both;
}

@for $i from 1 through 6 {
  .tn-auth__fields > :nth-child(#{$i}) {
    animation-delay: #{.66 + $i * .07}s;
  }
}

.tn-field-label {
  display: block;
  margin-bottom: 6px;
  color: var(--tn-text-2);
  font-size: 12.5px;
  font-weight: 600;
  letter-spacing: .01em;
}

.tn-field-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;

  .tn-field-label {
    margin-bottom: 0;
  }
}

.tn-auth-field.v-text-field {
  margin-bottom: 16px;
  font-size: 14px;

  > .v-input__control > .v-input__slot {
    min-height: 44px !important;
    border-radius: 10px;
    background: #F8FAFC;
    transition: background .2s, box-shadow .2s;
  }

  &.v-input--is-focused > .v-input__control > .v-input__slot {
    background: #FFFFFF;
    box-shadow: 0 0 0 4px rgba(214, 138, 45, .14);
  }

  .v-input__prepend-inner,
  .v-input__append-inner {
    align-self: center;
    margin-top: 0 !important;
  }

  .v-input__prepend-inner .v-icon,
  .v-input__append-inner .v-icon {
    font-size: 19px;
  }

  &:not(.v-input--is-focused):not(.v-input--has-state) .v-icon {
    color: #94A3B8 !important;
  }

  .v-text-field__details {
    margin-bottom: 0 !important;
    padding-top: 2px;
  }
}

// Navy button with a gold sheen that sweeps across on hover.
.tn-auth__submit.v-btn {
  height: 46px !important;
  margin-top: 6px;
  overflow: hidden;
  border-radius: 10px;
  background: linear-gradient(135deg, #0F1B2D 0%, #1B3150 100%) !important;
  color: #FFFFFF !important;
  font-size: 14.5px;
  letter-spacing: .01em;
  box-shadow: 0 10px 22px -10px rgba(15, 27, 45, .7) !important;
  transition: transform .2s, box-shadow .2s;

  // Vuetify's white hover/focus overlay would wash the navy out; the sheen replaces it.
  &::before {
    display: none;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: -60%;
    width: 50%;
    background: linear-gradient(105deg, transparent, rgba(242, 194, 123, .45), transparent);
    transform: skewX(-18deg);
    transition: left .7s $tn-ease;
  }

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 14px 26px -10px rgba(15, 27, 45, .75), 0 0 0 1px rgba(214, 138, 45, .5) !important;

    &::after {
      left: 120%;
    }

    .tn-auth__submit-arrow {
      transform: translateX(4px);
    }
  }

  &:focus-visible {
    box-shadow: 0 0 0 3px #FFFFFF, 0 0 0 5px #D68A2D !important;
  }
}

.tn-auth__submit-arrow.v-icon {
  color: #F2C27B !important;
  transition: transform .25s $tn-ease;
}

.tn-link-btn {
  padding: 0;
  border: 0;
  background: none;
  color: var(--v-gold-base) !important;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;

  .v-icon {
    color: inherit !important;
  }

  &:hover {
    text-decoration: underline;
  }
}

.tn-auth__back {
  margin-top: 18px;
  text-align: center;
  animation: tn-fade-in .6s 1.2s ease-out both;
}

// ---------- Animations ----------
@keyframes tn-kenburns {
  from { transform: scale(1) translate3d(0, 0, 0); }
  to { transform: scale(1.12) translate3d(-2%, -1.5%, 0); }
}

@keyframes tn-drift-a {
  from { transform: translate3d(0, 0, 0) scale(1); }
  to { transform: translate3d(30%, 24%, 0) scale(1.25); }
}

@keyframes tn-drift-b {
  from { transform: translate3d(0, 0, 0) scale(1.1); }
  to { transform: translate3d(-26%, -30%, 0) scale(.9); }
}

@keyframes tn-route-flow {
  to { stroke-dashoffset: -130; }
}

@keyframes tn-rise {
  0% { transform: translate3d(0, 0, 0); opacity: 0; }
  12% { opacity: .9; }
  70% { opacity: .55; }
  100% { transform: translate3d(18px, -105vh, 0); opacity: 0; }
}

@keyframes tn-spin {
  to { transform: rotate(360deg); }
}

@keyframes tn-spin-reverse {
  to { transform: rotate(-360deg); }
}

@keyframes tn-breathe {
  from { transform: scale(.92); opacity: .75; }
  to { transform: scale(1.06); opacity: 1; }
}

@keyframes tn-fade-in {
  from { opacity: 0; }
}

@keyframes tn-logo-in {
  from { opacity: 0; transform: translate3d(0, -14px, 0) scale(.96); filter: blur(8px); }
}

@keyframes tn-shine {
  0% { background-position: 150% 0; }
  35%, 100% { background-position: -50% 0; }
}

@keyframes tn-fade-up {
  from { opacity: 0; transform: translate3d(0, 10px, 0); }
}

@keyframes tn-card-in {
  from { opacity: 0; transform: translate3d(0, 28px, 0) scale(.97); }
}

@keyframes tn-orbit {
  to { --tn-angle: 360deg; }
}

@media (prefers-reduced-motion: reduce) {
  .tn-auth *,
  .tn-auth *::before,
  .tn-auth *::after {
    animation: none !important;
  }

  .tn-auth__dust,
  .tn-auth__routes circle {
    display: none;
  }
}
</style>
